import { Backdrop, Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Modal, Select, TextField } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { MainContext } from '../../../../context/main/main-context';
import DataHandler from '../../../../handlers/DataHandler';
import { sweetStatusAlert } from '../../../../components/common/SweetAlert';

function AddSection({ isOpen, setIsOpen, currentSection, setCurrentSection,handlefetchAllSections }) {
    const { createSection, addSection, templateMetaData, handleUpdateSection, updateSection } = useContext(MainContext);

    const [loading, setLoading] = useState(false);
    const [newSection, setNewSection] = useState({
        template_id: currentSection?.template_id || '',
        part_id: currentSection?.part_id || 0,
        name: currentSection?.name || '',
        title: currentSection?.title || '',
        description: currentSection?.description || '',
        no_of_questions: currentSection?.no_of_questions || 0,
        question_type_id: currentSection?.question_type_id || null,
        status_id: currentSection?.status_id === 0 ? 0 : 1,
        notation_id: currentSection?.notation_id || 2
    });
    const [filteredParts, setFilteredParts] = useState([]);
    const [errors, setErrors] = useState({
        name: "",
        template_id: "",
        part_id: "",
        title: "",
        no_of_questions: "",
        question_type_id: "",
        status_id: ""
    });

    const romanToInt = (roman) => {
        const romanMap = {
            I: 1, V: 5, X: 10, L: 50, C: 100
        };
        let sum = 0;
        let prev = 0;

        roman.toUpperCase().split('').reverse().forEach(char => {
            const val = romanMap[char];
            if (val < prev) {
                sum -= val;
            } else {
                sum += val;
                prev = val;
            }
        });
        return sum;
    };

    const filterPartsForTemplate = (template_id) => {
        const selectedTemplate = templateMetaData.templates.find(
            (t) => t.template_id === parseInt(template_id)
        );

        if (!selectedTemplate) return [];

        const { part_notation_id, no_of_parts } = selectedTemplate;

        const selectedNotation = templateMetaData.notations.find(
            (n) => n.notation_id === part_notation_id
        );

        let parts = templateMetaData.parts.filter(
            (part) => part.notation_id === part_notation_id
        );

        if (selectedNotation.notation_id === 1) {
            parts = parts.sort((a, b) => a.name.localeCompare(b.name));
        } else if (selectedNotation.notation_id === 2) {
            parts = parts.sort((a, b) => parseInt(a.name) - parseInt(b.name));
        } else if (selectedNotation.notation_id === 3) {
            parts = parts.sort((a, b) => romanToInt(a.name) - romanToInt(b.name));
        }

        return parts.slice(0, no_of_parts);
    };

    const handleChange = (field) => (e) => {
        if (!e) return;
        let value = e.target ? e.target.value : e;
        // if (field === 'no_of_questions') {
        //     value = parseInt(value) || 0;
        // }
        setNewSection(prev => ({ ...prev, [field]: value }));

        // validate on every change
        validateSection(field, value);

        if (field === "name") {
            const error = validateSectionName(newSection.notation_id, value);
            setErrors((prev) => ({ ...prev, name: error }));
        }

        if (field === "notation_id") {
            const error = validateSectionName(value, newSection.name);
            setErrors((prev) => ({ ...prev, name: error }));
        }

        if (field === "template_id") {
            const value = e.target.value;
            const filtered = filterPartsForTemplate(value);
            setFilteredParts(filtered);
        }
    };

    const handleCreate = async () => {
        try {
            setLoading(true);
            const user_id = DataHandler.getFromSession('manage_user_id');
            await createSection?.(newSection, user_id);
        } catch (error) {
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to create section", "error");
        }
    };

    const handleUpdate = async () => {
        try {
            setLoading(true);
            const user_id = DataHandler.getFromSession('manage_user_id');
            await handleUpdateSection?.(newSection, user_id, currentSection.section_id);
        } catch (error) {
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to update template", "error");
        }
    };

    const handleClose = () => {
        setNewSection({
            template_id: '',
            part_id: '',
            name: '',
            title: '',
            description: '',
            no_of_questions: 0,
            question_type_id: '',
            status_id: 0
        });
        setCurrentSection(null);
        setIsOpen(false);
    };

    const validateSection = (field, value) => {
        let error = "";

        switch (field) {
            case "name":
                error = validateSectionName(newSection.notation_id, value);
                break;

            case "template_id":
                if (!value) {
                    error = "Template is required.";
                }
                break;

            case "part_id":
                if (!value) {
                    error = "Part is required.";
                }
                break;

            // case "question_type_id":
            //     if (!value) {
            //         error = "Question Type is required.";
            //     }
            //     break;

            case "status_id":
                if (!value) {
                    error = "Status is required.";
                }
                break;

            case "title":
                if (!value.trim()) {
                    error = "Section Title is required.";
                }
                else if (doesSectionFieldExist("title", value)) {
                    error = "Section title already exists under this template and part.";
                }
                break;

            // case "no_of_questions":
            //     if (!value || value < 1) {
            //         error = "Enter valid number of questions (min 1).";
            //     }
            //     break;

            default:
                break;
        }

        setErrors(prev => ({ ...prev, [field]: error }));
        return error;
    };

    const doesSectionFieldExist = (field, value) => {
        return templateMetaData?.sections?.some(
            (section) =>
                section.template_id === newSection.template_id &&
                section.part_id === newSection.part_id &&
                section[field]?.trim().toLowerCase() === value.trim().toLowerCase() &&
                section.section_id !== currentSection?.section_id 
        );
    };

    const validateSectionName = (notationId, value) => {
        if (!value) {
            return "Section Name is required.";
        }

        if (doesSectionFieldExist("name", value)) {
            return "Section name already exists under this template and part.";
        }

        switch (notationId) {
            case 1: 
                if (!/^[A-Z]$/.test(value)) {
                    return "Enter a single uppercase letter (A-Z).";
                }
                break;

            case 2: 
                if (!/^[1-9]\d*$/.test(value)) {
                    return "Enter a positive integer (1,2,3,...).";
                }
                break;

            case 3: 
                if (!/^(I|II|III|IV|V|VI|VII|VIII|IX|X)$/.test(value.toUpperCase())) {
                    return "Enter valid Roman numeral (I to X).";
                }
                break;

            default:
                return null;
        }

        return null;
    };

    useEffect(() => {
        if (addSection && addSection.success) {
            setLoading(true);
            sweetStatusAlert("Success", "Section created successfully", "success");
            handlefetchAllSections()
            handleClose();
        }
        else if (addSection && !addSection.success) {
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to create section", "error");
        }
    }, [addSection]);

    useEffect(() => {

        if (updateSection && updateSection.success) {
            setLoading(true);
            sweetStatusAlert("Success", "Section updated successfully", "success");
            handlefetchAllSections()
            handleClose();
        }
        else if (updateSection && !updateSection.success) {
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to update section", "error");
        }
    }, [updateSection]);

    useEffect(() => {
        const error = validateSectionName(newSection.notation_id, newSection.name);
        setErrors({ name: error });
    }, []);

    useEffect(() => {
        Object.keys(newSection).forEach(field => {
            validateSection(field, newSection[field]);
        });
    }, []);

    useEffect(() => {
        if (currentSection?.template_id && templateMetaData?.templates?.length > 0) {
            const filtered = filterPartsForTemplate(currentSection.template_id);
            setFilteredParts(filtered);
        }
    }, [currentSection, templateMetaData]);

    const isFormValid = () => {
        return Object.values(errors).every(err => !err)
            && newSection.name
            && newSection.title
            && newSection.template_id
            && newSection.part_id
            // && newSection.question_type_id
            && newSection.status_id
            // && newSection.no_of_questions >= 1;
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            disableEscapeKeyDown={loading}
            sx={{
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: -1,
                }
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: 800,
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                    zIndex: 1,
                    overflowY: 'auto',
                }}
            >
                <Backdrop
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: (theme) => theme.zIndex.modal + 1,
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        borderRadius: '8px'
                    }}
                    open={loading}
                >
                    <CircularProgress color="primary" />
                </Backdrop>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <Grid container spacing={2} >
                        {/* Column 1 */}
                        {/* <Grid item xs={6} > */}

                        <FormControl fullWidth sx={{ mb: 2 }}  error={Boolean(errors.template_id)}>
                                <InputLabel>Template</InputLabel>
                                <Select
                                    value={newSection.template_id}
                                    onChange={handleChange("template_id")}
                                    label="Template"
                                    disabled={loading}
                                >
                                    {templateMetaData?.templates?.map(
                                        (template) => (
                                            <MenuItem key={template.template_id} value={template.template_id}>
                                                {template.name}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                                {errors.template_id && (
                                    <p style={{ color: '#d32f2f', margin: '3px 14px 0 14px', fontSize: '0.75rem' }}>
                                        {errors.template_id}
                                    </p>
                                )}
                            </FormControl>

                            <FormControl fullWidth sx={{ mb: 2 }} error={Boolean(errors.part_id)}>
                                <InputLabel>Part</InputLabel>
                                <Select
                                    value={newSection.part_id}
                                    onChange={handleChange("part_id")}
                                    label="Part"
                                    disabled={loading}
                                    helperText={errors.part_id}
                                >
                                    {filteredParts?.map(
                                        (part) => (
                                            <MenuItem disabled={part.status_id === 0} key={part.part_id} value={part.part_id}>
                                                {part.name}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                                {errors.part_id && (
                                    <p style={{ color: '#d32f2f', margin: '3px 14px 0 14px', fontSize: '0.75rem' }}>
                                        {errors.part_id}
                                    </p>
                                )}
                            </FormControl>

                            {/* <FormControl fullWidth sx={{ mb: 2 }} error={Boolean(errors.question_type_id)}>
                                <InputLabel>Question Type</InputLabel>
                                <Select
                                    value={newSection.question_type_id}
                                    onChange={handleChange("question_type_id")}
                                    label="Question Type"
                                    disabled={loading}
                                >
                                    {templateMetaData?.question_type?.map(
                                        (qtype) => (
                                            <MenuItem key={qtype.type_id} value={qtype.type_id}>
                                                {qtype.qtype}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                                 {errors.question_type_id && (
                                    <p style={{ color: '#d32f2f', margin: '3px 14px 0 14px', fontSize: '0.75rem' }}>
                                        {errors.question_type_id}
                                    </p>
                                )}
                            </FormControl> */}

                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>Section Notation</InputLabel>
                                <Select
                                    value={newSection.notation_id || 2}
                                    onChange={handleChange("notation_id")}
                                    label="Section Notation"
                                    disabled={loading}
                                >
                                    {templateMetaData?.notations?.map((notation) => (
                                        <MenuItem key={notation.notation_id} value={notation.notation_id}>
                                            {notation.notation_type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                sx={{ mb: 2 }}
                                value={newSection.name}
                                onChange={handleChange("name")}
                                label="Section Name"
                                variant="outlined"
                                disabled={loading}
                                error={Boolean(errors.name)}
                                helperText={errors.name}
                            />

                            <TextField
                                fullWidth
                                sx={{ mb: 2 }}
                                value={newSection.title}
                                onChange={handleChange("title")}
                                label="Section Title"
                                variant="outlined"
                                disabled={loading}
                                error={Boolean(errors.title)}
                                helperText={errors.title}
                            />

                            <TextField
                                fullWidth
                                sx={{ mb: 2 }}
                                value={newSection.description}
                                onChange={handleChange("description")}
                                label="Description"
                                variant="outlined"
                                disabled={loading}
                                multiline
                                rows={3}
                            />

                            {/* <TextField
                                fullWidth
                                sx={{ mb: 2 }}
                                value={newSection.no_of_questions}
                                onChange={handleChange("no_of_questions")}
                                label="No of questions"
                                variant="outlined"
                                type="number"
                                inputProps={{ min: 1 }}
                                disabled={loading}
                                error={Boolean(errors.no_of_questions)}
                                helperText={errors.no_of_questions}
                            /> */}
                        {/* </Grid> */}

                        {/* Column 2 */}
                        {/* <Grid item xs={6} sx={{ml:'100px'}}> */}
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={newSection.status_id}
                                    onChange={handleChange("status_id")}
                                    label="Status"
                                    disabled={loading}
                                >
                                    {[{ value: "Active", key: 1 }, { value: "Inactive", key: 0 }].map(
                                        (option) => (
                                            <MenuItem key={option.key} value={option.key}>
                                                {option.value}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                            </FormControl>

                            
                        {/* </Grid> */}
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        {currentSection && currentSection.section_id ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleUpdate}
                                disabled={!isFormValid() || loading}
                            >
                                Update
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleCreate}
                                disabled={!isFormValid() || loading}
                            >
                                Create
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </Box>
                </FormControl>
            </Box>
        </Modal>
    );
}

export default AddSection;